import * as React from 'react';
import {
  type PropsWithChildren,
  type ReactNode,
  type Ref,
  createContext,
  useContext,
  useImperativeHandle,
  useMemo,
  useReducer,
} from 'react'
import type { ConversationApi } from '../types.js'

// ---------------------------------------------------------------------
// Context definition
// ---------------------------------------------------------------------

type ConversationApiState = {
  overlayContent: ReactNode
  waitingForResponse: boolean
}

const conversationApiState = createContext<ConversationApiState>({
  overlayContent: null,
  waitingForResponse: false,
})

const conversationApi = createContext<ConversationApi>({
  setOverlay: () => {},
  setWaitingForResponse: () => {},
})

// ---------------------------------------------------------------------
// initialize the context values
// ---------------------------------------------------------------------

type ConversationApiAction =
  | {
      type: 'setOverlayContent'
      children: ReactNode
    }
  | {
      type: 'setWaitingForResponse'
      value: boolean
    }

function apiReducer(state: ConversationApiState, action: ConversationApiAction): ConversationApiState {
  switch (action.type) {
    case 'setOverlayContent':
      return {
        ...state,
        overlayContent: action.children,
      }

    case 'setWaitingForResponse':
      if (action.value === state.waitingForResponse) return state

      return {
        ...state,
        waitingForResponse: action.value,
      }

    default:
      return state
  }
}

const initialState: ConversationApiState = {
  overlayContent: null,
  waitingForResponse: false,
}

function useCreateContext() {
  const [state, dispatch] = useReducer(apiReducer, initialState)

  const api = useMemo<ConversationApi>(
    () => ({
      setOverlay: children => {
        dispatch({ type: 'setOverlayContent', children })
      },
      setWaitingForResponse: value => {
        dispatch({ type: 'setWaitingForResponse', value })
      },
    }),
    [],
  )

  return { api, state }
}

// ---------------------------------------------------------------------
// Context provider
// ---------------------------------------------------------------------

type ConversationApiProviderProps = PropsWithChildren<{
  api?: Ref<ConversationApi>
}>

export function ConversationApiProvider(props: ConversationApiProviderProps) {
  const { children, api: apiRef } = props
  const { api, state } = useCreateContext()

  useImperativeHandle(apiRef, () => api, [api])

  return (
    <conversationApi.Provider value={api}>
      <conversationApiState.Provider value={state}>{children}</conversationApiState.Provider>
    </conversationApi.Provider>
  )
}

// ---------------------------------------------------------------------
// Context hooks
// ---------------------------------------------------------------------

export function useConversationApi() {
  return useContext(conversationApi)
}

export function useOverlayContent() {
  return useContext(conversationApiState).overlayContent
}

export function useWaitingForResponse() {
  return useContext(conversationApiState).waitingForResponse
}
